import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Article as ArticleIcon,
  Work as WorkIcon,
  ContactMail as ContactMailIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Celebration as CelebrationIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import {
  collection, onSnapshot, query, where, getDocs, addDoc, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';

function Dashboard() {
  const [stats, setStats] = useState({
    members: 0,
    news: 0,
    jobs: 0,
    contacts: 0
  });
  const [loading, setLoading] = useState(true);
  const [notices, setNotices] = useState([]);
  const [notice, setNotice] = useState('');
  const [openNoticeDialog, setOpenNoticeDialog] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [greeting, setGreeting] = useState(null);
  const [openGreetingDialog, setOpenGreetingDialog] = useState(false);
  const [newGreeting, setNewGreeting] = useState({
    imageUrl: '',
    active: true
  });
  const [uploadProgress, setUploadProgress] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (500KB = 500 * 1024 bytes)
    if (file.size > 500 * 1024) {
      alert('Image size must be less than 500KB');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      setUploadProgress(true);

      // Create a reference to the storage location
      const storageRef = ref(storage, `greetings/${Date.now()}_${file.name}`);

      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update the greeting state with the new image URL
      setNewGreeting(prev => ({
        ...prev,
        imageUrl: downloadURL
      }));

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadProgress(false);
    }
  };

  const handleGreetingSubmit = async () => {
    if (!newGreeting.imageUrl.trim()) return;

    try {
      // Delete old greeting image if exists
      if (greeting?.imageUrl) {
        try {
          const oldImageRef = ref(storage, greeting.imageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.error('Error deleting old image:', error);
        }
      }

      // Deactivate current greeting if exists
      if (greeting) {
        await deleteDoc(doc(db, 'greetings', greeting.id));
      }

      // Add new greeting
      await addDoc(collection(db, 'greetings'), {
        ...newGreeting,
        timestamp: serverTimestamp()
      });

      setNewGreeting({
        imageUrl: '',
        active: true
      });
      setOpenGreetingDialog(false);
    } catch (error) {
      console.error('Error updating greeting:', error);
      alert('Failed to update greeting. Please try again.');
    }
  };

  const handleDeleteGreeting = async () => {
    if (!greeting) return;

    try {
      // Delete image from storage
      if (greeting.imageUrl) {
        try {
          const imageRef = ref(storage, greeting.imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      }

      // Delete greeting document
      await deleteDoc(doc(db, 'greetings', greeting.id));
      setGreeting(null);
    } catch (error) {
      console.error('Error deleting greeting:', error);
    }
  };

  useEffect(() => {
    // Fetch notices
    const noticesUnsubscribe = onSnapshot(
      query(collection(db, 'notices'), where('active', '==', true)),
      (snapshot) => {
        const noticesList = [];
        snapshot.forEach((doc) => {
          noticesList.push({ id: doc.id, ...doc.data() });
        });
        setNotices(noticesList.sort((a, b) => b.timestamp - a.timestamp));
      }
    );

    // Fetch current greeting
    const greetingUnsubscribe = onSnapshot(
      query(collection(db, 'greetings'), where('active', '==', true)),
      (snapshot) => {
        if (!snapshot.empty) {
          setGreeting({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
        } else {
          setGreeting(null);
        }
      }
    );

    // Fetch other stats
    const fetchStats = async () => {
      const collections = ['members', 'news', 'jobs', 'contacts'];
      const newStats = { ...stats };

      for (const col of collections) {
        const snapshot = await getDocs(collection(db, col));
        newStats[col] = snapshot.size;
      }

      setStats(newStats);
    };

    fetchStats();
    return () => {
      noticesUnsubscribe();
      greetingUnsubscribe();
    };
  }, []);

  const handleNoticeSubmit = async () => {
    if (!notice.trim()) return;

    try {
      if (editingNotice) {
        // Update existing notice
        await deleteDoc(doc(db, 'notices', editingNotice.id));
      }

      // Add new notice
      await addDoc(collection(db, 'notices'), {
        text: notice.trim(),
        timestamp: serverTimestamp(),
        active: true
      });

      setNotice('');
      setEditingNotice(null);
      setOpenNoticeDialog(false);
    } catch (error) {
      console.error('Error updating notice:', error);
    }
  };

  const handleEditNotice = (notice) => {
    setEditingNotice(notice);
    setNotice(notice.text);
    setOpenNoticeDialog(true);
  };

  const handleDeleteNotice = async (noticeId) => {
    try {
      await deleteDoc(doc(db, 'notices', noticeId));
    } catch (error) {
      console.error('Error deleting notice:', error);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, ml: { xs: 0, md: '240px' } }}>
      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#580100', mb: 3 }}>
          Dashboard
        </Typography>

        {/* Greeting Section */}
        <Paper sx={{ p: 2, mb: 4, borderLeft: '4px solid #580100' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CelebrationIcon sx={{ color: '#580100', mr: 1.5 }} />
              <Typography variant="h6" sx={{ color: '#580100' }}>
                Popup Message
              </Typography>
            </Box>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenGreetingDialog(true)}
                sx={{ mr: 1 }}
              >
                Update Greeting
              </Button>
              {greeting && (
                <IconButton 
                  color="error" 
                  onClick={handleDeleteGreeting}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Box>
          {greeting ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <img 
                  src={greeting.imageUrl} 
                  alt="Greeting" 
                  style={{ 
                    width: 100, 
                    height: 100, 
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }} 
                />
                <Typography sx={{ color: '#666' }}>
                  {greeting.text}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Last updated: {greeting.timestamp?.toDate().toLocaleString()}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
              No active greeting
            </Typography>
          )}
        </Paper>

        {/* Notice Section */}
        <Paper sx={{ p: 2, mb: 4, borderLeft: '4px solid #580100' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationsIcon sx={{ color: '#580100', mr: 1.5 }} />
              <Typography variant="h6" sx={{ color: '#580100' }}>
                Notices
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setEditingNotice(null);
                setNotice('');
                setOpenNoticeDialog(true);
              }}
            >
              Add Notice
            </Button>
          </Box>

          {notices.length > 0 ? (
            notices.map((notice) => (
              <Paper
                key={notice.id}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 1,
                  bgcolor: '#f8f8f8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography sx={{ color: '#666', flex: 1 }}>
                  {notice.text}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditNotice(notice)}
                    sx={{ color: '#580100', mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteNotice(notice.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))
          ) : (
            <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
              No active notices
            </Typography>
          )}
        </Paper>

        {/* Stats Overview */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, bgcolor: '#fff', borderLeft: '4px solid #580100' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ fontSize: 28, color: '#580100', mr: 1.5 }} />
                <Box>
                  <Typography variant="h5" sx={{ color: '#580100', fontWeight: 500 }}>
                    {stats.members}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Members
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, bgcolor: '#fff', borderLeft: '4px solid #580100' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArticleIcon sx={{ fontSize: 28, color: '#580100', mr: 1.5 }} />
                <Box>
                  <Typography variant="h5" sx={{ color: '#580100', fontWeight: 500 }}>
                    {stats.news}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    News Articles
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, bgcolor: '#fff', borderLeft: '4px solid #580100' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WorkIcon sx={{ fontSize: 28, color: '#580100', mr: 1.5 }} />
                <Box>
                  <Typography variant="h5" sx={{ color: '#580100', fontWeight: 500 }}>
                    {stats.jobs}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Jobs
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, bgcolor: '#fff', borderLeft: '4px solid #580100' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ContactMailIcon sx={{ fontSize: 28, color: '#580100', mr: 1.5 }} />
                <Box>
                  <Typography variant="h5" sx={{ color: '#580100', fontWeight: 500 }}>
                    {stats.contacts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact Requests
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Notice Dialog */}
        <Dialog 
          open={openNoticeDialog} 
          onClose={() => {
            setOpenNoticeDialog(false);
            setEditingNotice(null);
            setNotice('');
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{editingNotice ? 'Edit Notice' : 'Add Notice'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Notice Text"
              fullWidth
              multiline
              rows={3}
              value={notice}
              onChange={(e) => setNotice(e.target.value)}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenNoticeDialog(false);
              setEditingNotice(null);
              setNotice('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleNoticeSubmit} variant="contained" color="primary">
              {editingNotice ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Greeting Dialog */}
        <Dialog 
          open={openGreetingDialog} 
          onClose={() => {
            setOpenGreetingDialog(false);
            setNewGreeting({
              imageUrl: '',
              active: true
            });
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Update Greeting Image</DialogTitle>
          <DialogContent>
            {/* Image Upload Section */}
            <Box sx={{ mb: 2 }}>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
                ref={fileInputRef}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {newGreeting.imageUrl ? (
                  <Box sx={{ position: 'relative', width: '100%', textAlign: 'center' }}>
                    <img
                      src={newGreeting.imageUrl}
                      alt="Greeting Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'error.dark'
                        }
                      }}
                      onClick={() => setNewGreeting(prev => ({ ...prev, imageUrl: '' }))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      component="span"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadProgress}
                      startIcon={uploadProgress ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      sx={{
                        p: 5,
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        width: '100%',
                        '&:hover': {
                          borderColor: 'primary.dark'
                        }
                      }}
                    >
                      {uploadProgress ? 'Uploading...' : 'Upload Greeting Image'}
                    </Button>
                    <Typography variant="caption" color="textSecondary">
                      Maximum file size: 500KB
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenGreetingDialog(false);
              setNewGreeting({
                imageUrl: '',
                active: true
              });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleGreetingSubmit} 
              variant="contained" 
              color="primary"
              disabled={!newGreeting.imageUrl.trim() || uploadProgress}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default Dashboard;
